import React, { useState } from 'react';
import { FiMail, FiArrowLeft } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';

/**
 * @component ResetPasswordForm
 * @description Formulario para solicitar restablecimiento de contraseña
 * @vibe-color 🟠 #F59E0B
 * @vibe-icon 🔑
 */
const ResetPasswordForm: React.FC = () => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  
  const validateForm = (): boolean => {
    if (!email) {
      setError('Introduce tu dirección de email');
      return false;
    }
    
    // Validación básica de email con regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Introduce un email válido');
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      await resetPassword(email);
      setSuccessMessage(`Hemos enviado un enlace de recuperación a ${email}`);
      setEmail(''); // Limpiar el campo email después del éxito
    } catch (err: unknown) {
      const error = err as Error & { code?: string };
      if (error?.code === 'auth/user-not-found') {
        setError('No existe cuenta con este email');
      } else {
        setError(`Error al enviar el correo: ${error?.message || 'Desconocido'}`);
      }
      console.error('Error de recuperación:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
      <h2 className="text-2xl font-bold mb-2 text-center text-primary-700">
        Recupera tu contraseña
      </h2>
      <p className="text-center text-gray-600 mb-6">
        Introduce tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
      </p>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md border border-green-200">
          {successMessage}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="relative">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Correo electrónico
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiMail className="text-gray-400" />
            </div>
            <input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="tu@email.com"
            />
          </div>
        </div>
        
        <div>
          <button
            type="submit"
            disabled={loading}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enviando...
              </>
            ) : 'Enviar enlace de recuperación'}
          </button>
        </div>
        
        <div className="flex items-center justify-center text-sm mt-4">
          <Link to="/auth/login" className="flex items-center text-primary-600 hover:text-primary-500">
            <FiArrowLeft className="mr-1" /> Volver al inicio de sesión
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ResetPasswordForm;
